import {WorkerBriefings} from '../../supervisor/WorkerBriefings'
import { WorkerAssignmentReadinessPanel } from '../WorkerAssignmentReadiness'
import React, { useMemo, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService, type WorkerAssignment } from '../../../../services/worker'
import { openDirections, hasDestination } from '../../../../services/mapsDirections'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import type { BrowserPlatform } from '../../../../hooks/useBrowserPlatform'
import {
  MobileCapabilityNotice,
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobilePrimaryButton,
  MobileSecondaryButton,
  MobileSectionTitle,
  MobileSheet,
  statusTone,
} from './WorkerMobilePrimitives'
import { useWorkerPunchActions } from './useWorkerPunchActions'
import type { QueuedMutation } from '../../../../services/offlineWorkerQueue'
import { WorkerAssignmentMap } from '../WorkerAssignmentMap'

/**
 * Worker-visible reason a queued action failed. Client-generated failures carry
 * a stable code and are localized here; a backend-authored message is shown
 * verbatim, as backend guidance is elsewhere in the worker UI.
 */
const queuedFailureReason = (row: QueuedMutation, t: (key: string) => string): string => {
  if (row.failureCode === 'expired_beyond_sync_window') {
    return t('Recorded too long ago to submit automatically. Ask your supervisor to record it.')
  }
  if (row.failureCode === 'missing_location') {
    return t('Recorded without a location, so it cannot be submitted automatically.')
  }
  return row.lastError || t('This could not be submitted. Ask your supervisor for help.')
}

/**
 * Worker home — the web mirror of the Android worker cockpit.
 *
 * Every action shown here is driven by a backend capability flag on the
 * assignment (`canPunchNow`, `canStartBreak`, `canReportRunningLate`, …). The
 * client never infers eligibility from assignment status, and the backend
 * re-evaluates geofence and punch eligibility on every submission.
 */

const isCancelled = (assignment: WorkerAssignment) =>
  ['cancelled', 'declined', 'rejected'].includes(String(assignment.assignmentStatus || assignment.assignment_status || ''))

const isRemoteAssignment = (assignment: WorkerAssignment) =>
  String(assignment.workMode || assignment.work_mode || '').toLowerCase() === 'remote'

const destinationOf = (assignment: WorkerAssignment) => ({
  name: assignment.destination?.name || assignment.location_name,
  address: assignment.destination?.displayAddress || assignment.location_address,
  latitude: assignment.destination?.latitude ?? assignment.latitude,
  longitude: assignment.destination?.longitude ?? assignment.longitude,
})

export const WorkerMobileHome: React.FC<{
  state: WorkerWorkspaceState
  platform: BrowserPlatform
  onOpenPermissions: () => void
  onOpenLearning: () => void
  onOpenTab: (tab: 'shifts' | 'messages' | 'time' | 'profile') => void
}> = ({ state, platform, onOpenPermissions, onOpenLearning, onOpenTab }) => {
  const { t, formatDateTime, formatTime } = useI18n()
  const { data, busy, run } = state
  const punch = useWorkerPunchActions({ state, platform })
  const [attendanceFor, setAttendanceFor] = useState<WorkerAssignment | null>(null)

  const today = useMemo(() => data.today.filter((assignment) => !isCancelled(assignment)), [data.today])
  const current = today.find((assignment) => assignment.hasActiveWorkerShift) || today[0] || null
  const next = data.upcoming[0] || null
  const onBreak = data.punch?.state === 'BREAK' || data.punch?.currentPunchStatus === 'break'
  const punchState = String(current?.currentPunchState || data.punch?.currentPunchStatus || 'OUT')
  const punchedIn = punchState === 'IN' || punchState === 'BREAK'
  // Backend flag: the worker already completed a shift on this assignment today,
  // so Android labels the action "Resume shift" rather than "Punch in".
  const canResume = Boolean(current?.canResumeNow ?? current?.can_resume_now)
  const unreadMessages = data.threads.reduce((total, thread) => total + (thread.unreadCount || 0), 0)
  const unreadNotifications = data.notifications.filter((row) => !row.readAt).length
  const documentIssues = current?.documentEligibility?.eligible === false
    ? (current.documentEligibility.issues || [])
    : []

  const locationBlocked = platform.features.location.availability === 'browser_unsupported'
    || platform.features.location.availability === 'permission_denied'

  return (
    <div className="wm-screen" data-testid="worker-mobile-home">
      <WorkerBriefings/>
      {platform.queue.pending + platform.queue.syncing + platform.queue.failed > 0 ? (
        <MobileCard tone="warning" testId="worker-mobile-offline-banner">
          <MobileSectionTitle
            title={t('Waiting to sync')}
            detail={t('These actions are saved on this device and stay visible until the server confirms them.')}
          />
          <div className="wm-chip-row">
            {platform.queue.pending ? <MobileChip tone="warning">{t('{count} pending sync', { count: String(platform.queue.pending) })}</MobileChip> : null}
            {platform.queue.syncing ? <MobileChip tone="info">{t('{count} syncing', { count: String(platform.queue.syncing) })}</MobileChip> : null}
            {platform.queue.failed ? <MobileChip tone="critical">{t('{count} failed', { count: String(platform.queue.failed) })}</MobileChip> : null}
            {platform.queue.requiresReview ? <MobileChip tone="warning">{t('{count} awaiting review', { count: String(platform.queue.requiresReview) })}</MobileChip> : null}
          </div>
          <MobileSecondaryButton
            label={platform.online ? t('Sync now') : t('Waiting for a connection')}
            onClick={() => void platform.syncNow()}
            disabled={!platform.online}
            testId="worker-mobile-sync-now"
          />
          {platform.failed.length ? (
            <div className="wm-inline-card" data-testid="worker-mobile-failed-actions">
              <strong>{t('These could not be submitted')}</strong>
              <ul className="wm-list">
                {platform.failed.map((row) => (
                  <li key={row.id}>{queuedFailureReason(row, t)}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </MobileCard>
      ) : null}

      {!platform.online ? (
        <MobileCapabilityNotice
          tone="warning"
          testId="worker-mobile-offline-notice"
          title={t('You are offline')}
          detail={t('Punch and attendance actions are queued on this device and submitted when you reconnect. Nothing is recorded on the server until then.')}
        />
      ) : null}

      {locationBlocked ? (
        <MobileCapabilityNotice
          tone="warning"
          testId="worker-mobile-location-notice"
          title={platform.features.location.availability === 'browser_unsupported'
            ? t('Location is not available in this browser')
            : t('Location permission is blocked')}
          detail={platform.features.location.availability === 'browser_unsupported'
            ? t('Onsite punches need a browser that can share your location. Open the app in Chrome or Safari on your phone.')
            : t('Your browser is blocking location for this site. Re-enable it in browser settings, then return here.')}
          action={{ label: t('Open permissions'), onClick: onOpenPermissions }}
        />
      ) : null}

      {current ? (
        <MobileCard tone={onBreak ? 'warning' : punchState === 'IN' ? 'positive' : 'neutral'} testId="worker-mobile-punch-card">
          <div className="wm-card__heading">
            <div>
              <small>{current.company_org_name || current.companyBranding?.displayName || t('Assignment')}</small>
              <h2>{current.job_title || t('Assigned shift')}</h2>
              <p>
                {current.starts_at || current.startsAt
                  ? formatDateTime(String(current.startsAt || current.starts_at), { dateStyle: 'medium', timeStyle: 'short' })
                  : t('Time pending')}
                {current.ends_at || current.endsAt ? ` – ${formatTime(String(current.endsAt || current.ends_at))}` : ''}
              </p>
            </div>
            <MobileChip tone={statusTone(current.assignmentStatus || current.assignment_status)}>
              {t(String(current.assignmentStatus || current.assignment_status || 'assigned').replace(/_/g, ' '))}
            </MobileChip>
          </div>

          <WorkerAssignmentReadinessPanel assignmentId={current.id} />
          <div className="wm-meta-row">
            <span>{t(String(current.workMode || current.work_mode || 'onsite'))}</span>
            {current.payLabel || current.pay_label ? <span>{current.payLabel || current.pay_label}</span> : null}
          </div>

          {current.location_name || current.location_address ? (
            <div className="wm-worksite">
              <span className="wm-worksite__pin" aria-hidden="true" />
              <div>
                <strong>{current.location_name || t('Worksite')}</strong>
                {current.location_address ? <small>{current.location_address}</small> : null}
              </div>
              {!isRemoteAssignment(current) && hasDestination(destinationOf(current)) ? (
                <MobileSecondaryButton
                  label={t('Directions')}
                  onClick={() => openDirections(destinationOf(current))}
                  testId="worker-mobile-directions"
                />
              ) : null}
            </div>
          ) : null}

          {current.jobMapFeatureEnabled ? <WorkerAssignmentMap assignment={current} compact /> : null}

          {current.punchWindowReason ? <p className="wm-guidance">{current.punchWindowReason}</p> : null}
          {punch.locationMessage ? <p className="wm-guidance" role="status">{punch.locationMessage}</p> : null}

          {/*
            Control hierarchy mirrors the Android worker cockpit: while a worker
            is on shift the break control is the primary action and punching out
            is secondary, so the destructive end-of-shift action is never the
            giant button sitting under a worker's thumb mid-shift.
          */}
          <div className="wm-punch-actions">
            {punchedIn ? (
              <>
                <MobilePrimaryButton
                  label={onBreak ? t('Return to work') : t('Start break')}
                  tone={onBreak ? 'positive' : 'warning'}
                  testId={onBreak ? 'worker-mobile-break-end' : 'worker-mobile-break-start'}
                  busy={busy === `punch-${current.id}`}
                  disabled={Boolean(busy) || (onBreak ? current.canEndBreak === false : current.canStartBreak !== true)}
                  onClick={() => void punch.submitPunch(onBreak ? 'break-end' : 'break-start', current)}
                />
                <MobileSecondaryButton
                  label={t('Punch out')}
                  testId="worker-mobile-punch"
                  disabled={Boolean(busy) || current.canPunchOut === false}
                  onClick={() => void punch.submitPunch('punch-out', current)}
                />
              </>
            ) : (
              <MobilePrimaryButton
                label={canResume ? t('Resume shift') : t('Punch in')}
                tone="positive"
                testId="worker-mobile-punch"
                busy={busy === `punch-${current.id}`}
                disabled={Boolean(busy) || current.canPunchNow !== true}
                onClick={() => void punch.submitPunch('punch-in', current)}
              />
            )}
          </div>

          <div className="wm-secondary-actions">
            {current.canMarkOnWay ? (
              <MobileSecondaryButton
                label={current.onTheWayAt ? t('On the way sent') : t('On the way')}
                testId="worker-mobile-on-the-way"
                disabled={Boolean(busy) || Boolean(current.onTheWayAt)}
                onClick={() => void punch.markOnTheWay(current)}
              />
            ) : null}
            {current.canReportRunningLate || current.canReportCannotAttend || current.canRequestCancelAssignment ? (
              <MobileSecondaryButton
                label={t('Report a problem')}
                testId="worker-mobile-attendance-open"
                disabled={Boolean(busy)}
                onClick={() => setAttendanceFor(current)}
              />
            ) : null}
          </div>

          {current.cancellationRequest?.status ? (
            <p className="wm-guidance">
              {t('Cancellation request: {status}', { status: t(String(current.cancellationRequest.status).replace(/_/g, ' ')) })}
            </p>
          ) : null}
        </MobileCard>
      ) : (
        <MobileEmptyState
          testId="worker-mobile-no-shift"
          title={t('No shift today')}
          detail={t('When you are assigned a shift it appears here with your punch controls.')}
        />
      )}

      {documentIssues.length ? (
        <MobileCard tone="critical" testId="worker-mobile-compliance">
          <MobileSectionTitle title={t('Documents need attention')} detail={t('These were flagged by your organization for this assignment.')} />
          <ul className="wm-list">
            {documentIssues.map((issue, index) => (
              <li key={`${issue.label || 'issue'}-${index}`}>{issue.label || issue.reason}</li>
            ))}
          </ul>
          <MobileSecondaryButton label={t('Open documents')} onClick={() => onOpenTab('profile')} />
        </MobileCard>
      ) : null}

      {next ? (
        <MobileCard testId="worker-mobile-next-shift">
          <MobileSectionTitle title={t('Next shift')} />
          <div className="wm-card__heading">
            <div>
              <h3>{next.job_title || t('Assigned shift')}</h3>
              <p>{next.starts_at || next.startsAt
                ? formatDateTime(String(next.startsAt || next.starts_at), { dateStyle: 'medium', timeStyle: 'short' })
                : t('Time pending')}</p>
              <small>{next.location_name || t('Location pending')}</small>
            </div>
            <MobileChip tone={statusTone(next.assignmentStatus || next.assignment_status)}>
              {t(String(next.assignmentStatus || next.assignment_status || 'assigned').replace(/_/g, ' '))}
            </MobileChip>
          </div>
          <WorkerAssignmentReadinessPanel assignmentId={next.id} />
          <MobileSecondaryButton label={t('All shifts')} onClick={() => onOpenTab('shifts')} />
        </MobileCard>
      ) : null}

      <MobileCard testId="worker-mobile-quick-links">
        <MobileSectionTitle title={t('Your day')} />
        <div className="wm-quick-grid">
          <button type="button" className="wm-quick" onClick={() => onOpenTab('messages')}>
            <span>{t('Messages')}</span>
            {unreadMessages ? <MobileChip tone="critical">{String(unreadMessages)}</MobileChip> : null}
          </button>
          <button type="button" className="wm-quick" onClick={() => onOpenTab('shifts')}>
            <span>{t('Find work')}</span>
            {data.recommended.length ? <MobileChip tone="info">{String(data.recommended.length)}</MobileChip> : null}
          </button>
          <button type="button" className="wm-quick" onClick={onOpenLearning}>
            <span>{t('Learning')}</span>
          </button>
          <button type="button" className="wm-quick" onClick={() => onOpenTab('profile')}>
            <span>{t('Profile')}</span>
            {unreadNotifications ? <MobileChip tone="warning">{String(unreadNotifications)}</MobileChip> : null}
          </button>
        </div>
      </MobileCard>

      <AttendanceSheet
        assignment={attendanceFor}
        busy={busy}
        onClose={() => setAttendanceFor(null)}
        onSubmit={async (assignment, reportType, notes) => {
          setAttendanceFor(null)
          await run(
            `attendance-${assignment.id}`,
            () => workerService.reportAttendance(assignment.id, reportType, notes),
            t('Your report was sent to your organization.')
          )
        }}
      />
    </div>
  )
}

/**
 * Attendance exception sheet. Each option appears ONLY when the backend says
 * the worker may take it, so an action can never be offered on local status.
 */
const AttendanceSheet: React.FC<{
  assignment: WorkerAssignment | null
  busy: string
  onClose: () => void
  onSubmit: (assignment: WorkerAssignment, reportType: 'running_late' | 'cannot_attend' | 'request_cancellation', notes: string) => Promise<void>
}> = ({ assignment, busy, onClose, onSubmit }) => {
  const { t } = useI18n()
  const [reportType, setReportType] = useState<'running_late' | 'cannot_attend' | 'request_cancellation'>('running_late')
  const [notes, setNotes] = useState('')

  // Options come straight from the backend's per-assignment capability flags.
  const options = React.useMemo(() => (assignment ? ([
    assignment.canReportRunningLate ? { value: 'running_late' as const, label: t('Running late') } : null,
    assignment.canReportCannotAttend ? { value: 'cannot_attend' as const, label: t('Cannot attend') } : null,
    assignment.canRequestCancelAssignment ? { value: 'request_cancellation' as const, label: t('Request cancellation') } : null,
  ].filter(Boolean) as Array<{ value: 'running_late' | 'cannot_attend' | 'request_cancellation'; label: string }>) : []),
  [assignment, t])

  React.useEffect(() => {
    if (options.length && !options.some((option) => option.value === reportType)) setReportType(options[0].value)
  }, [options, reportType])

  return (
    <MobileSheet
      title={t('Report a problem')}
      open={Boolean(assignment)}
      onClose={onClose}
      testId="worker-mobile-attendance-sheet"
    >
      {!options.length ? (
        <p>{t('No attendance actions are available for this shift right now.')}</p>
      ) : (
        <form
          className="wm-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (assignment) void onSubmit(assignment, reportType, notes.trim())
          }}
        >
          <fieldset className="wm-radio-group">
            <legend>{t('What is happening?')}</legend>
            {options.map((option) => (
              <label key={option.value} className="wm-radio">
                <input
                  type="radio"
                  name="worker-attendance-report"
                  value={option.value}
                  checked={reportType === option.value}
                  onChange={() => setReportType(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </fieldset>
          <label className="wm-field">
            <span>{t('Add a note (optional)')}</span>
            <textarea
              value={notes}
              rows={3}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t('Your organization sees this note.')}
            />
          </label>
          <button type="submit" className="wm-button wm-button--primary" data-tone="info" disabled={Boolean(busy)}>
            {t('Send report')}
          </button>
        </form>
      )}
    </MobileSheet>
  )
}
